import { useCallback, useEffect, useRef, useState } from 'react'
import { BrowserMultiFormatReader } from '@zxing/browser'
import { BarcodeFormat, DecodeHintType } from '@zxing/library'
import { conteosStockApi } from '../../api/conteosStock'
import { colors, font, radius, sx, text } from '../../design/tokens'
import { fmtHoraDe } from '../../utils/fecha'
import { BotonPrimario, Card, Cargando, ErrorBox, Seccion, Titulo } from './comunes'

// Inventario cíclico (Martin, 29 oct 2026 — docs/diseno-inventario-ciclico.md):
// escanear → cargar lo contado → LGR graba la hora del servidor y cruza con los
// movimientos de Presea hasta esa hora. Acá solo se cuenta; el ajuste se
// aprueba después, desde la compu. Mismo lector que /m/fotos.
const HINTS = new Map([[DecodeHintType.POSSIBLE_FORMATS, [BarcodeFormat.EAN_13, BarcodeFormat.EAN_8, BarcodeFormat.UPC_A, BarcodeFormat.UPC_E]]])

const errorDe = (e, porDefecto) => {
  const errs = e.response?.data?.errors
  return errs ? Object.values(errs).flat().join(' ') : (e.response?.data?.message || porDefecto)
}

const fmtCant = (n) => (n == null ? '—' : `${n > 0 ? '+' : ''}${Number(n).toLocaleString('es-AR', { maximumFractionDigits: 2 })}`)

export default function MovilConteo() {
  const videoRef = useRef(null)
  const controlesRef = useRef(null)
  const cantidadRef = useRef(null)
  const [config, setConfig] = useState(null)
  const [deposito, setDeposito] = useState(null)
  const [escaneando, setEscaneando] = useState(false)
  const [codigo, setCodigo] = useState('')
  const [buscando, setBuscando] = useState(false)
  const [articulos, setArticulos] = useState(null)
  const [articulo, setArticulo] = useState(null)
  const [cantidad, setCantidad] = useState('')
  const [guardando, setGuardando] = useState(false)
  const [tanda, setTanda] = useState([])
  const [error, setError] = useState('')

  useEffect(() => {
    conteosStockApi.config().then(({ data }) => {
      setConfig(data)
      setDeposito(data.depositos[0]?.codigo ?? null)
    }).catch(() => setError('No se pudo cargar la configuración del inventario.'))
  }, [])

  const cargarTanda = useCallback(() => {
    if (!deposito) return
    conteosStockApi.listar({ deposito, mios: 1 }).then(({ data }) => setTanda(data.conteos)).catch(() => {})
  }, [deposito])
  useEffect(() => { cargarTanda() }, [cargarTanda])

  // Mientras haya conteos calculándose (~5 s contra Presea), se refresca solo.
  useEffect(() => {
    if (!tanda.some(c => c.estado === 'pendiente' && !c.error)) return undefined
    const t = setTimeout(cargarTanda, 3000)
    return () => clearTimeout(t)
  }, [tanda, cargarTanda])

  const parar = useCallback(() => {
    controlesRef.current?.stop()
    controlesRef.current = null
    setEscaneando(false)
  }, [])
  useEffect(() => parar, [parar])

  const elegir = (a) => {
    setArticulo(a)
    setTimeout(() => cantidadRef.current?.focus(), 50)
  }

  const buscar = useCallback(async (valor) => {
    const v = valor.trim()
    if (!v) return
    const esEan = /^\d{8,}$/.test(v)
    setBuscando(true); setError(''); setArticulos(null); setArticulo(null); setCantidad('')
    try {
      const { data } = await conteosStockApi.articulos({ ...(esEan ? { ean: v } : { sku: v.toUpperCase() }), deposito })
      setArticulos(data.articulos)
      if (data.articulos.length === 1) elegir(data.articulos[0])
    } catch (e) {
      setError(errorDe(e, 'No se pudo buscar el artículo.'))
    } finally { setBuscando(false) }
  }, [deposito])

  const escanear = async () => {
    setError(''); setEscaneando(true)
    window.scrollTo(0, 0)
    try {
      const lector = new BrowserMultiFormatReader(HINTS)
      controlesRef.current = await lector.decodeFromConstraints(
        { video: { facingMode: { ideal: 'environment' } } },
        videoRef.current,
        (resultado, _err, controles) => {
          if (!resultado) return
          controles.stop()
          controlesRef.current = null
          setEscaneando(false)
          if (navigator.vibrate) navigator.vibrate(60)
          setCodigo(resultado.getText())
          buscar(resultado.getText())
        },
      )
    } catch (e) {
      setEscaneando(false)
      setError(e?.name === 'NotAllowedError'
        ? 'El navegador no dio permiso para usar la cámara. Habilitala en Ajustes → Safari → Cámara, o tipeá el código.'
        : 'No se pudo abrir la cámara. Tipeá el código.')
    }
  }

  const guardar = async (e) => {
    e.preventDefault()
    if (!articulo || cantidad === '') return
    const reemplazar = !!articulo.conteo_hoy
    if (reemplazar && !window.confirm(`Ya contaste ${articulo.sku} hoy (${articulo.conteo_hoy.cantidad} a las ${fmtHoraDe(articulo.conteo_hoy.contado_at)}). ¿Reemplazar por ${cantidad}?`)) return
    setGuardando(true); setError('')
    try {
      await conteosStockApi.crear({ articulo_id: articulo.id, deposito, cantidad: Number(cantidad), reemplazar })
      setArticulo(null); setArticulos(null); setCodigo(''); setCantidad('')
      cargarTanda()
    } catch (err) {
      setError(errorDe(err, 'No se pudo guardar el conteo.'))
    } finally { setGuardando(false) }
  }

  const depoNombre = config?.depositos.find(d => d.codigo === deposito)?.nombre

  return (
    <div>
      <Titulo>Conteo de inventario</Titulo>
      {depoNombre && <p style={st.nota}>Contando en <b>{depoNombre}</b>. La hora la pone el sistema al guardar: contá el producto completo antes de cargarlo.</p>}

      <div style={{ ...st.visor, display: escaneando ? 'block' : 'none' }}>
        <video ref={videoRef} style={st.video} muted playsInline />
        <div style={st.guia} />
      </div>
      {escaneando
        ? <button onClick={parar} style={st.btnSec}>Cancelar</button>
        : <BotonPrimario onClick={escanear} disabled={!deposito} style={!deposito ? st.deshabilitado : undefined}>📷 Escanear código</BotonPrimario>}

      <form onSubmit={(e) => { e.preventDefault(); buscar(codigo) }} style={st.manual}>
        <input value={codigo} onChange={e => setCodigo(e.target.value)} placeholder="o tipeá el EAN o el SKU" autoCapitalize="characters" autoCorrect="off" spellCheck={false} style={st.input} />
        <button type="submit" style={{ ...st.btnSec, width: 'auto', flexShrink: 0 }}>Buscar</button>
      </form>

      <ErrorBox>{error}</ErrorBox>
      {buscando && <Cargando />}

      {articulos && articulos.length === 0 && (
        <Card style={{ marginTop: 12 }}><p style={st.nota}>No hay artículos con ese código en LGR.</p></Card>
      )}
      {articulos && articulos.length > 1 && !articulo && (
        <Seccion titulo="¿Cuál de estos es?">
          {articulos.map(a => (
            <button key={a.id} onClick={() => elegir(a)} style={st.opcion}>
              <span style={{ minWidth: 0 }}>
                <span style={st.nombre}>{a.descripcion}</span>
                <span style={st.sub}><span style={st.mono}>{a.sku}</span></span>
              </span>
              <span style={st.flecha}>›</span>
            </button>
          ))}
        </Seccion>
      )}

      {articulo && (
        <Card style={{ marginTop: 12 }}>
          <div style={st.nombre}>{articulo.descripcion}</div>
          <div style={st.sub}><span style={st.mono}>{articulo.sku}</span>{articulo.ean ? <> · <span style={st.mono}>{articulo.ean}</span></> : null}</div>
          {articulo.conteo_hoy && (
            <div style={st.aviso}>Ya lo contaste hoy: {articulo.conteo_hoy.cantidad} a las {fmtHoraDe(articulo.conteo_hoy.contado_at)}. Si lo cargás de nuevo, vale el último.</div>
          )}
          {!articulo.codigo_presea
            ? <div style={st.aviso}>Este artículo no tiene código de Presea: no se puede cruzar con su stock.</div>
            : (
              <form onSubmit={guardar}>
                <label style={st.label}>Botellas contadas</label>
                <input ref={cantidadRef} value={cantidad} onChange={e => setCantidad(e.target.value.replace(/\D+/g, ''))} inputMode="numeric" placeholder="" style={{ ...st.input, width: '100%', fontSize: text.display, fontWeight: 600, textAlign: 'center' }} />
                <BotonPrimario type="submit" disabled={guardando || cantidad === ''} style={{ marginTop: 12, ...(guardando || cantidad === '' ? st.deshabilitado : {}) }}>
                  {guardando ? 'Guardando…' : '✓ Guardar conteo'}
                </BotonPrimario>
              </form>
            )}
        </Card>
      )}

      {tanda.length > 0 && (
        <Seccion titulo="Contados hoy">
          {tanda.map(c => <FilaConteo key={c.id} c={c} />)}
        </Seccion>
      )}
    </div>
  )
}

function FilaConteo({ c }) {
  const calculando = c.estado === 'pendiente'
  const descartado = c.estado === 'descartado'
  const revisar = c.estado === 'reconteo'
  return (
    <div style={{ ...st.fila, opacity: descartado ? 0.5 : 1 }}>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={st.nombre}>{c.descripcion}</div>
        <div style={st.sub}><span style={st.mono}>{c.sku}</span> · <span style={st.mono}>{fmtHoraDe(c.contado_at)}</span> · contó <span style={st.mono}>{c.cantidad}</span></div>
        {c.error && <div style={{ ...st.sub, color: colors.err }}>{c.error}</div>}
        {!calculando && (c.marcas || []).map((m, i) => <div key={i} style={{ ...st.sub, color: colors.warn }}>⚠ {m.texto}</div>)}
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        {descartado ? <span style={st.chip}>Reemplazado</span>
          : calculando ? <span style={{ ...st.chip, ...(c.error ? st.chipErr : {}) }}>{c.error ? 'Sin calcular' : 'Calculando…'}</span>
            : (
              <>
                {revisar && <span style={{ ...st.chip, ...st.chipWarn, marginBottom: 4 }}>Revisar</span>}
                <div style={{ ...st.mono, fontSize: 18, fontWeight: 700, color: c.diferencia === 0 ? colors.ok : colors.text }}>{fmtCant(c.diferencia)}</div>
                <div style={st.sub}>Presea <span style={st.mono}>{c.teorico == null ? '—' : Number(c.teorico).toLocaleString('es-AR')}</span></div>
              </>
            )}
      </div>
    </div>
  )
}

const mono = sx.mono
const st = {
  mono,
  aviso: { background: colors.warnBg, color: colors.warn, borderRadius: radius.lg, padding: '10px 12px', fontSize: text.body, marginTop: 10, fontFamily: font.sans },
  visor: { position: 'relative', borderRadius: 14, overflow: 'hidden', background: colors.codeBg, marginBottom: 10 },
  video: { width: '100%', display: 'block', aspectRatio: '4 / 3', objectFit: 'cover' },
  guia: { position: 'absolute', left: '12%', right: '12%', top: '45%', height: 2, background: colors.errStrong, opacity: 0.8 },
  btnSec: { padding: '13px 16px', borderRadius: 12, border: `1px solid ${colors.border}`, background: colors.bg, color: colors.text, fontSize: 15, fontWeight: 600, fontFamily: font.sans, cursor: 'pointer', width: '100%', boxSizing: 'border-box', minHeight: 44 },
  manual: { display: 'flex', gap: 8, marginTop: 10 },
  input: { flex: 1, minWidth: 0, padding: '12px 12px', borderRadius: 12, border: `1px solid ${colors.textPlh}`, fontSize: 16, color: colors.text, background: colors.bg, outline: 'none', boxSizing: 'border-box', ...mono },
  label: { display: 'block', fontSize: text.sec, color: colors.textSec, margin: '14px 0 6px', fontFamily: font.sans },
  opcion: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, width: '100%', padding: '12px 0', minHeight: 44, border: 'none', borderBottom: `1px solid ${colors.borderSoft}`, background: 'none', textAlign: 'left', cursor: 'pointer' },
  flecha: { color: colors.textMeta, fontSize: 20, flexShrink: 0 },
  fila: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: `1px solid ${colors.borderSoft}` },
  nombre: { display: 'block', fontSize: text.body, color: colors.text, fontFamily: font.sans },
  sub: { display: 'block', fontSize: text.sec, color: colors.textSec, fontFamily: font.sans },
  nota: { fontSize: text.sec, color: colors.textSec, margin: '0 0 10px', fontFamily: font.sans },
  // Geometría de EstadoBadge (la misma del badge de la pantalla web de Inventario).
  chip: { ...sx.badge, background: colors.bgSoft, color: colors.textSec, fontFamily: font.sans, whiteSpace: 'nowrap' },
  chipWarn: { display: 'table', marginLeft: 'auto', background: colors.warnBg, color: colors.warn },
  chipErr: { background: colors.errBg, color: colors.err },
  deshabilitado: { opacity: 0.55, cursor: 'not-allowed' },
}
